import { useState } from 'react';
import { CalendarRange, ChevronDown, ChevronUp, MapPin, RotateCcw, Sparkles } from 'lucide-react';
import type { MaturityChartFacets } from '@/types/cultivar';
import { EMPTY_MATURITY_FILTERS, type MaturityFilterState } from '@/utils/maturityChart';

/**
 * The controls above the maturity timeline.
 *
 * Every option offered is one the data can satisfy — seasons, regions and crop
 * types all come from the server's facets. A picker listing a region with
 * nothing in it produces an empty chart and no explanation, which reads as a
 * broken page rather than an empty filter.
 */

interface Props {
  facets: MaturityChartFacets;
  value: MaturityFilterState;
  onChange: (next: MaturityFilterState) => void;
  /** The season the server fell back to when none was chosen, so the control can show it. */
  effectiveSeasons: number[];
}

/** Add or remove one id, leaving the rest alone. */
function toggle(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function Chip({
  active, onClick, children, swatch,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  swatch?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
        active
          ? 'border-primary-300 bg-primary-50 text-primary-800 font-medium'
          : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
      }`}
    >
      {swatch && (
        <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: swatch }} />
      )}
      <span className="truncate max-w-[10rem]">{children}</span>
    </button>
  );
}

export function MaturityFilterBar({ facets, value, onChange, effectiveSeasons }: Props) {
  const [regionsOpen, setRegionsOpen] = useState(false);
  const set = (patch: Partial<MaturityFilterState>) => onChange({ ...value, ...patch });

  const dirty =
    value.seasonYears.length > 0 || value.allSeasons || value.regionIds.length > 0 || value.cropTypeIds.length > 0 ||
    value.fromDate !== '' || value.toDate !== '' || !value.includeEstimates;

  // With no explicit pick the server chose for us; showing that choice as
  // selected is the difference between "nothing is filtered" and a season
  // number the user cannot see but is nonetheless looking at.
  const shownSeasons = value.seasonYears.length > 0 ? value.seasonYears : effectiveSeasons;

  // Long lists collapse; a short one is always open, because a disclosure
  // triangle hiding three regions is just a click in the way.
  const manyRegions = facets.regions.length > 6;
  const regionsVisible = !manyRegions || regionsOpen;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 space-y-4">
      {/* Seasons */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-semibold text-neutral-700 flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5" /> Season
          </span>
          {dirty && (
            <button
              type="button"
              onClick={() => onChange({ ...EMPTY_MATURITY_FILTERS, groupBy: value.groupBy })}
              className="text-xs text-neutral-500 hover:text-neutral-800 flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          )}
        </div>
        {facets.seasons.length === 0 ? (
          <p className="text-xs text-neutral-500">No seasons carry maturity data yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {facets.seasons.length > 1 && (
              <Chip
                active={value.allSeasons}
                onClick={() => set({ allSeasons: !value.allSeasons, seasonYears: [] })}
              >
                All seasons
              </Chip>
            )}
            {facets.seasons.map((y) => (
              <Chip
                key={y}
                active={value.allSeasons || shownSeasons.includes(y)}
                onClick={() => set({
                  allSeasons: false,
                  // Leaving "all" by clicking one year keeps that year, rather
                  // than toggling it off a list that was never explicit.
                  seasonYears: value.allSeasons ? [y] : toggle(value.seasonYears, y),
                })}
              >
                {y}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {/* Regions */}
      {facets.regions.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => manyRegions && setRegionsOpen((o) => !o)}
            className="flex items-center justify-between gap-2 w-full mb-2"
            aria-expanded={regionsVisible}
          >
            <span className="text-xs font-semibold text-neutral-700 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Region
              {value.regionIds.length > 0 && (
                <span className="text-primary-700 font-normal">({value.regionIds.length})</span>
              )}
            </span>
            {manyRegions && (regionsOpen
              ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400" />
              : <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />)}
          </button>
          {regionsVisible && (
            <div className="flex flex-wrap gap-1.5">
              {facets.regions.map((r) => (
                <Chip
                  key={r.id}
                  active={value.regionIds.includes(r.id)}
                  onClick={() => set({ regionIds: toggle(value.regionIds, r.id) })}
                >
                  {r.name}
                </Chip>
              ))}
            </div>
          )}
          {value.regionIds.length === 0 && regionsVisible && (
            <p className="text-[11px] text-neutral-400 mt-1.5">All regions</p>
          )}
        </div>
      )}

      {/* Date window */}
      <div>
        <span className="text-xs font-semibold text-neutral-700 block mb-2">Picking between</span>
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label="Picking from"
            value={value.fromDate}
            onChange={(e) => set({ fromDate: e.target.value })}
            className="input-professional flex-1 min-w-0 text-xs"
          />
          <span className="text-xs text-neutral-400 shrink-0">to</span>
          <input
            type="date"
            aria-label="Picking to"
            value={value.toDate}
            onChange={(e) => set({ toDate: e.target.value })}
            className="input-professional flex-1 min-w-0 text-xs"
          />
        </div>
        {value.fromDate !== '' && value.toDate !== '' && value.toDate < value.fromDate && (
          <p className="text-[11px] text-warning-700 mt-1.5">
            The end of the window is before its start, so nothing can match.
          </p>
        )}
      </div>

      {/* Grouping and estimates */}
      <div className="space-y-2 pt-1 border-t border-neutral-100">
        <div className="pt-2">
          <span className="text-xs font-semibold text-neutral-700 block mb-2">Group by</span>
          <div className="flex gap-1.5">
            {([
              ['crop_type', 'Crop type'],
              ['region', 'Region'],
              ['none', 'None'],
            ] as const).map(([key, label]) => (
              <Chip key={key} active={value.groupBy === key} onClick={() => set({ groupBy: key })}>
                {label}
              </Chip>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-neutral-700 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={value.includeEstimates}
            onChange={(e) => set({ includeEstimates: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            <span className="flex items-center gap-1 font-medium">
              <Sparkles className="h-3 w-3" /> Include calculated estimates
            </span>
            <span className="text-neutral-500 block mt-0.5">
              Fills a region's gaps from the same cultivar picked elsewhere. Drawn hollow, never
              as a measurement.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

export default MaturityFilterBar;
